import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectEditingCollection,
  selectMergeConnected,
  selectSelectedEmployeeId,
  selectSelectedPersonaId,
  selectUser,
  setEditingCollection,
  setMergeConnected,
  setSelectedMenu,
  setSelectedMenuId,
  setSelectedPersonaId,
  setSelectedEmployeeId,
  selectResourcesSearchText,
  setResourcesSearchText,
  setUIStates,
  selectUIStates,
} from "../../redux/features/appData";
import {
  apiDeleteAllCollectionResources,
  apiDeleteCollectionFolderAndResources,
  apiEmbedCollection,
  apiGetCollection,
  apiGetCollectionLinkRecords,
  apiResyncCollection,
} from "../../services/CollectionService";
import {
  EmbedCollectionResult,
  Employee,
  LinkRecord,
  LinkRecordFull,
  SyncProgress,
} from "winslow-core";
import {
  getSyncProgressAsLabel,
  getSyncProgressCSSBackgroundColor,
  downloadFile,
} from "winslow-core";
import "./CollectionResources.css";
import { FilterText } from "../shares/FilterText";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../config";
import { apiDeleteLinkRecord } from "../../services/LinkRecordService";
import { useCoreDataLoader } from "../../hooks/useCoreDataLoader";
import ModalDeleteResourceConfirm from "./ModalDeleteResourceConfirm";
import ModalDeleteAllResourcesConfirm from "./ModalDeleteAllResourcesConfirm";
import { PersonaExpanded, PersonaLinkRecordOrientation } from "winslow-core";
import { apiGetPersonas } from "../../services/PersonasService";
import {
  apiCreatePersonaLinkRecord,
  apiUpdatePersonaLinkRecord,
  apiDeletePersonaLinkRecord,
} from "../../services/PersonaLinkRecordService";
import { ResourceCard } from "./ResourceCard";
import { PersonaSelector } from "../common/PersonaSelector";
import { PersonaCard } from "../personas/PersonaCard";
import { ModalEmployeeResourceSearch } from "./ModalEmployeeResourceSearch";
import {
  apiGetEmployeeLinkRecords,
  apiGetEmployees,
} from "../../services/EmployeeService";
import { SelectedEmployeeCard } from "../employees/SelectedEmployeeCard";
import { MENUS } from "../config/menus";
import { apiGetWorkspace } from "../../api";
import ModalHRISRequired from "./ModalHRISRequired";
import { MyTooltip } from "../common/MyTooltip";
import { cli } from "webpack";
import clipboardCopy from "clipboard-copy";
import { ResourceActionsSidebar } from "./ResourceActionsSidebar";
import { LogsIcon } from "lucide-react";
import config from "environment";

export const CollectionResources = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { collectionId, linkRecordId } = useParams();
  const { reloadAll } = useCoreDataLoader(true);

  const storeUser = useSelector(selectUser);
  const currentCollection = useSelector(selectEditingCollection);
  const [selectedLinkRecordId, setSelectedLinkRecordId] = useState<
    string | undefined
  >(undefined);
  const [collectionlinkRecords, setCollectionLinkRecords] = useState<
    LinkRecordFull[] | null
  >(null);
  const [filteredLinkRecords, setFilteredLinkRecords] = useState<
    LinkRecordFull[] | null
  >(null);

  const debouncedSearchText = useSelector(selectResourcesSearchText);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showEmployeeSearch, setShowEmployeeSearch] = useState<boolean>(false);
  const [deletingLinkRecord, setDeletingLinkRecord] =
    useState<LinkRecord | null>(null);
  const [afterDelete, setAfterDelete] = useState<boolean>(false);

  const [showDeleteAllConfirm, setShowDeleteAllConfirm] =
    useState<boolean>(false);

  const [personas, setPersonas] = useState<PersonaExpanded[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [showPersonaSelector, setShowPersonaSelector] = useState<string | null>(
    null
  );
  const [personaLinkRecords, setPersonaLinkRecords] = useState<{
    [key: string]: {
      personaId: string;
      orientation: PersonaLinkRecordOrientation;
    }[];
  }>({});

  const selectedEmployeeId = useSelector(selectSelectedEmployeeId);
  const selectedPersonaId = useSelector(selectSelectedPersonaId);

  const mergeConnected = useSelector(selectMergeConnected);
  const [showHRISRequired, setShowHRISRequired] = useState<boolean>(false);

  const [isLoadingEmployeeLinks, setIsLoadingEmployeeLinks] =
    useState<boolean>(false);

  if (collectionId && linkRecordId) {
    localStorage.setItem(
      "stashUrl",
      `/collection/${collectionId}/resources/${linkRecordId}`
    );
  }

  const uiStates = useSelector(selectUIStates);

  useEffect(() => {
    if (storeUser && collectionId && !collectionlinkRecords) {
      (async () => {
        const [linkRecords, employees, workspace] = await Promise.all([
          loadLinkRecords(),
          loadEmployeeProfiles(),
          apiGetWorkspace(storeUser.currentWorkspaceId),
        ]);

        if (
          workspace &&
          workspace?.mergeAccountToken &&
          workspace.mergeAccountToken !== null
        ) {
          dispatch(setMergeConnected(true));
        } else {
          dispatch(setMergeConnected(false));
        }

        dispatch(setEditingCollection(await apiGetCollection(collectionId)));
        if (linkRecords && linkRecords.length > 0) {
          downloadLinkRecord(linkRecords);
          localStorage.removeItem("stashUrl");
        }

        if (employees) {
          setEmployees(employees);
        }
      })();
    }

    return () => {
      dispatch(setUIStates({ ...uiStates, resourceSidebarOpen: false }));
    };
  }, []);

  const matchesEmployee = (
    selectedEmployeeId: string | null,
    targetLinkRecordId: string,
    employeeLinkRecordIds: string[] | null
  ): boolean => {
    if (selectedEmployeeId && employeeLinkRecordIds) {
      return employeeLinkRecordIds.includes(targetLinkRecordId);
    }

    return true;
  };

  useEffect(() => {
    if (storeUser?.currentWorkspaceId) {
      apiGetPersonas(storeUser.currentWorkspaceId).then((personasResult) => {
        if (personasResult) {
          setPersonas(personasResult);

          // Create the personaLinkRecords map from the personas' link records
          const recordMap: {
            [key: string]: {
              personaId: string;
              orientation: PersonaLinkRecordOrientation;
            }[];
          } = {};

          personasResult.forEach((persona) => {
            persona.personaLinkRecords.forEach((record) => {
              if (!recordMap[record.linkRecordId]) {
                recordMap[record.linkRecordId] = [];
              }
              recordMap[record.linkRecordId].push({
                personaId: persona.id,
                orientation: record.orientation,
              });
            });
          });

          setPersonaLinkRecords(recordMap);
        }
      });
    }
  }, [storeUser]);

  const downloadLinkRecord = async (linkRecords: LinkRecordFull[]) => {
    if (linkRecordId && linkRecords) {
      const linkRecordToDownload = linkRecords.find(
        (lr) => lr.id === linkRecordId
      );
      if (linkRecordToDownload) {
        if (linkRecordToDownload.source === "LINK") {
          toast("Opening link...", toastSuccess);
          window.open(linkRecordToDownload.links[0].url, "__new");
          return;
        }

        toast("Downloading file...", toastSuccess);
        const downloadResult = await downloadFile(
          linkRecordToDownload.links[0].url,
          linkRecordToDownload?.links[0]?.title || "resource"
        );
        if (downloadResult) {
          toast("Downloaded", toastSuccess);
        } else {
          toast("Failed to download", toastFail);
        }
        return;
      } else {
        toast("Invalid - File Not Found", toastFail);
      }
    }
  };

  //ping the backend for new data until the collection is no longer processing
  useEffect(() => {
    if (
      collectionId &&
      currentCollection?.syncStatus === SyncProgress.PROCESSING
    ) {
      setTimeout(() => {
        loadLinkRecords();
        apiGetCollection(collectionId).then((col) => {
          dispatch(setEditingCollection(col));
        });
      }, 3000);
    }
  }, [currentCollection]);

  useEffect(() => {
    (async () => {
      await loadLinkRecords();
      setAfterDelete(false);
    })();
  }, [afterDelete]);

  useEffect(() => {
    if (!collectionlinkRecords) {
      return;
    }

    (async () => {
      let employeeLinkRecordIds: string[] | null = null;
      if (selectedEmployeeId) {
        setIsLoadingEmployeeLinks(true);
        try {
          employeeLinkRecordIds =
            (await apiGetEmployeeLinkRecords(
              selectedEmployeeId,
              collectionId!
            )) || null;
        } finally {
          setIsLoadingEmployeeLinks(false);
        }
      }

      const filtered = collectionlinkRecords.filter(
        (linkRecord: LinkRecordFull) => {
          // Text search filter
          const matchesSearch =
            !debouncedSearchText ||
            (linkRecord.links &&
              linkRecord.links.length > 0 &&
              linkRecord.links[0].title
                ?.toLowerCase()
                .includes(debouncedSearchText.toLowerCase())) ||
            (linkRecord.links &&
              linkRecord.links.length > 0 &&
              linkRecord.source === "LINK" &&
              linkRecord.links[0].url
                ?.toLowerCase()
                .includes(debouncedSearchText.toLowerCase()));

          // Handle NO_PERSONA case
          const matchesPersona =
            selectedPersonaId === "NO_PERSONA"
              ? !personaLinkRecords[linkRecord.id] ||
                personaLinkRecords[linkRecord.id].length === 0
              : !selectedPersonaId ||
                personaLinkRecords[linkRecord.id]?.some(
                  (record) => record.personaId === selectedPersonaId
                );

          const employeeMatches = matchesEmployee(
            selectedEmployeeId,
            linkRecord.id,
            employeeLinkRecordIds
          );
          return matchesSearch && matchesPersona && employeeMatches;
        }
      );

      setFilteredLinkRecords(filtered);
    })();
  }, [
    debouncedSearchText,
    selectedPersonaId,
    selectedEmployeeId,
    collectionlinkRecords,
    personaLinkRecords,
  ]);

  const loadEmployeeProfiles = async () => {
    if (!storeUser?.currentWorkspaceId) {
      return;
    }

    const employees = await apiGetEmployees(storeUser.currentWorkspaceId);
    return employees;
  };

  const loadLinkRecords = async () => {
    if (!collectionId) {
      return;
    }

    const linkRecords = await apiGetCollectionLinkRecords(collectionId);

    if (linkRecords) {
      linkRecords.sort((a: LinkRecordFull, b: LinkRecordFull) => {
        const aTitle =
          a.links && a.links.length > 0
            ? a.links[0].title
              ? a.links[0].title
              : ""
            : "";

        const bTitle =
          b.links && b.links.length > 0
            ? b.links[0].title
              ? b.links[0].title
              : ""
            : "";

        return aTitle.toLowerCase().localeCompare(bTitle.toLowerCase());
      });
    }

    setCollectionLinkRecords(linkRecords);
    setFilteredLinkRecords(linkRecords);

    return linkRecords;
  };

  const handleSearchChange = (text: string) => {
    dispatch(setResourcesSearchText(text));
  };

  const handleRecordClick = (linkRecord: LinkRecordFull) => {
    alert("Open record");
  };

  const deleteResource = async () => {
    if (!deletingLinkRecord || !currentCollection) {
      return;
    }

    await apiDeleteLinkRecord(deletingLinkRecord.id);
    toast("Deleted.", toastSuccess);

    //await apiEmbedCollection(currentCollection.id);

    reloadAll(storeUser?.currentWorkspaceId);
    setDeletingLinkRecord(null);
    setAfterDelete(true);
  };

  const handleOpenLink = (linkRecord: LinkRecordFull) => {
    if (!(linkRecord.links && linkRecord.links.length > 0)) {
      return;
    }

    if (linkRecord.source === "FILE" && linkRecord.links[0].title) {
      downloadFile(linkRecord.links[0].url, linkRecord.links[0].title);
      return;
    }

    window.open(linkRecord.links[0].url, "__new");
  };

  // this is called after the DELETE confirm modal is triggered
  const deleteAllResources = async () => {
    if (!collectionId || !storeUser) {
      return;
    }

    toast("Deleting all resources.", toastSuccess);

    const success = await apiDeleteAllCollectionResources(collectionId);

    if (!success) {
      toast("Problem deleting resources", toastFail);
    } else {
      toast("Success", toastSuccess);
    }

    reloadAll(storeUser.currentWorkspaceId);
    setAfterDelete(true);
  };

  const handleResync = async () => {
    if (!collectionId) {
      return;
    }

    toast("Resyncing, this make take a while.", toastSuccess);

    const result: EmbedCollectionResult | null =
      await apiResyncCollection(collectionId);

    dispatch(setSelectedEmployeeId(null));
    dispatch(setSelectedPersonaId(null));
    dispatch(setResourcesSearchText(""));

    if (!result || result === EmbedCollectionResult.FAIL) {
      toast("Resync Failed", toastFail);
    }

    if (result === EmbedCollectionResult.TOO_LARGE) {
      toast("Collection too large to resync", toastFail);
    }
  };

  const deleteFolder = async (linkRecord: LinkRecord) => {
    // console.log("[CollectionResources/deleteFolder]  linkRecord: ", linkRecord);

    if (!linkRecord.folderRootId || !currentCollection) {
      return;
    }

    toast("Deleting folder and it's resources..", toastSuccess);

    const collection = await apiDeleteCollectionFolderAndResources(
      currentCollection.id,
      linkRecord.folderRootId
    );

    toast("Updating the AI knowledge base...", toastSuccess);

    await apiEmbedCollection(currentCollection.id);

    // we've changes the core collection so update it
    reloadAll(storeUser?.currentWorkspaceId);
    setDeletingLinkRecord(null);
    setAfterDelete(true);
  };

  const handleAddPersona = async (linkRecordId: string, personaId: string) => {
    const orientation = PersonaLinkRecordOrientation.INCLUSIONARY;

    try {
      const result = await apiCreatePersonaLinkRecord({
        linkRecordId,
        personaId,
        orientation,
      });

      if (result.success) {
        setPersonaLinkRecords((prev) => ({
          ...prev,
          [linkRecordId]: [
            ...(prev[linkRecordId] || []),
            {
              personaId,
              orientation,
            },
          ],
        }));

        setPersonas((prevPersonas: PersonaExpanded[]) =>
          prevPersonas.map((persona: PersonaExpanded) =>
            persona.id === personaId
              ? {
                  ...persona,
                  personaLinkRecords: [
                    ...persona.personaLinkRecords,
                    {
                      id: "__new__",
                      linkRecordId,
                      orientation,
                      personaId,
                      createdAt: new Date(),
                      updatedAt: new Date(),
                    },
                  ],
                }
              : persona
          )
        );

        setShowPersonaSelector(null);
        toast("Persona added to resource", toastSuccess);
      } else {
        toast("Failed to add persona", toastFail);
      }
    } catch (error) {
      toast("Error adding persona", toastFail);
      console.error("Error adding persona:", error);
    }
  };

  const handleRemovePersona = async (
    linkRecordId: string,
    personaId: string
  ) => {
    try {
      const result = await apiDeletePersonaLinkRecord(linkRecordId, personaId);

      if (result.success) {
        setPersonaLinkRecords((prev) => ({
          ...prev,
          [linkRecordId]: prev[linkRecordId].filter(
            (p) => p.personaId !== personaId
          ),
        }));

        setPersonas((prevPersonas) =>
          prevPersonas.map((persona) =>
            persona.id === personaId
              ? {
                  ...persona,
                  personaLinkRecords: persona.personaLinkRecords.filter(
                    (plr) => plr.linkRecordId !== linkRecordId
                  ),
                }
              : persona
          )
        );

        toast("Persona removed from resource", toastSuccess);
      } else {
        toast("Failed to remove persona", toastFail);
      }
    } catch (error) {
      toast("Error removing persona", toastFail);
      console.error("Error removing persona:", error);
    }
  };

  const handleEmployeeSearchSelect = (employeeId: string) => {
    dispatch(setSelectedPersonaId(null));
    dispatch(setSelectedEmployeeId(employeeId));
    setShowEmployeeSearch(false);
  };

  const handlePersonaSelect = (personaId: string | null) => {
    dispatch(setSelectedEmployeeId(null));
    dispatch(setSelectedPersonaId(personaId));
  };

  const handleToggleOrientation = async (
    linkRecordId: string,
    personaId: string
  ) => {
    try {
      const currentRecord = personaLinkRecords[linkRecordId].find(
        (p) => p.personaId === personaId
      );

      if (!currentRecord) return;

      const newOrientation =
        currentRecord.orientation === PersonaLinkRecordOrientation.INCLUSIONARY
          ? PersonaLinkRecordOrientation.EXCLUSIONARY
          : PersonaLinkRecordOrientation.INCLUSIONARY;

      const result = await apiUpdatePersonaLinkRecord({
        linkRecordId,
        personaId,
        orientation: newOrientation,
      });

      if (result.success) {
        setPersonaLinkRecords((prev) => ({
          ...prev,
          [linkRecordId]: prev[linkRecordId].map((p) =>
            p.personaId === personaId
              ? {
                  ...p,
                  orientation: newOrientation,
                }
              : p
          ),
        }));
        toast("Persona orientation updated", toastSuccess);
      } else {
        toast("Failed to update persona orientation", toastFail);
      }
    } catch (error) {
      toast("Error updating persona orientation", toastFail);
      console.error("Error updating persona orientation:", error);
    }
  };

  const handleCopyCollectionToClipboard = async () => {
    if (!filteredLinkRecords) {
      return;
    }

    let text = "";

    filteredLinkRecords.forEach((linkRecord) => {
      if (linkRecord.links && linkRecord.links.length > 0) {
        let title = linkRecord.links[0].title || "(unknown)";
        title = title.replace(/[\n\t'"]/g, " "); // Replaces newlines and tabs with a space

        text += `"${linkRecord.links[0].url}"\t"${title}"\t${linkRecord.embeddedStatus}\n`;
      }
    });

    await clipboardCopy(text);
    toast("Collection copied to clipboard", toastSuccess);
  };

  const clearPersonaFilter = () => {
    dispatch(setSelectedPersonaId(null));
  };

  const clearEmployeeFilter = () => {
    dispatch(setSelectedEmployeeId(null));
  };

  const handleEmployeeButtonClick = () => {
    if (!mergeConnected) {
      setShowHRISRequired(true);
    } else {
      setShowEmployeeSearch(true);
    }
  };

  return (
    <div id="collection-resources">
      <div className="collection-resources-header">
        <div className="page-title" style={{ marginBottom: "0px" }}>
          Manage Resources
        </div>
        <div
          className="collection-resources-processing"
          style={{
            backgroundColor: getSyncProgressCSSBackgroundColor(
              currentCollection?.syncStatus || SyncProgress.ERRORED
            ),
          }}>
          {getSyncProgressAsLabel(
            currentCollection?.syncStatus || SyncProgress.ERRORED
          )}
        </div>
        <div className="collection-resources-header-buttons">
          <>
            <button
              className="collections-button-negative"
              onClick={() => {
                dispatch(setSelectedMenu(MENUS.CONFIGURE));
                dispatch(setSelectedMenuId(null));
                navigate("/create");
              }}>
              Add a Resource
            </button>
          </>
          {currentCollection?.linkRecords &&
            currentCollection.linkRecords.length > 0 && (
              <>
                <button
                  className="collections-button-negative"
                  onClick={() => {
                    setShowDeleteAllConfirm(true);
                    dispatch(setSelectedEmployeeId(null));
                    dispatch(setSelectedPersonaId(null));
                    dispatch(setResourcesSearchText(""));
                  }}>
                  Delete All
                </button>
                <button
                  className="collections-button-negative"
                  onClick={handleResync}>
                  Resync
                </button>
              </>
            )}
        </div>
      </div>

      {currentCollection?.linkRecords &&
        currentCollection.linkRecords.length > 0 && (
          <>
            <div className="collection-resources-search">
              <div className="collection-resources-search-input">
                <FilterText
                  onFilterChange={handleSearchChange}
                  bouncems={500}
                  text={debouncedSearchText}
                />
              </div>

              <div className="collection-resources-filter">
                <span>
                  <MyTooltip title="Employee Resource Search">
                    <div
                      className="collection-resources-filter-button"
                      onClick={handleEmployeeButtonClick}>
                      <img src="/icons/employee-button.svg" alt="Filter" />
                    </div>
                  </MyTooltip>
                </span>
              </div>

              <div className="collection-resources-employee-filter">
                <PersonaSelector
                  onChange={handlePersonaSelect}
                  openOnTop={false}
                  placeholder="Select Persona..."
                  personas={personas}
                  value={selectedPersonaId}
                />
              </div>
            </div>

            <div className="collection-resources-active-filter">
              {selectedPersonaId === "NO_PERSONA" ? (
                <>
                  <div className="collection-resources-employee-filter-title">
                    Showing resources with no personas:{" "}
                    {filteredLinkRecords?.length}
                  </div>
                  <div className="persona-card">
                    <div
                      className="persona-card-close"
                      onClick={(e) => {
                        clearPersonaFilter();
                      }}>
                      <img src="/icons/close.png" alt="Remove filter" />
                    </div>

                    <div className="persona-card-user-info">
                      <div className="persona-card-user-name"></div>
                    </div>
                    <div className="persona-card-content">
                      <div className="persona-card-title">No Persona</div>
                    </div>
                  </div>
                </>
              ) : (
                selectedPersonaId &&
                personas.length > 0 && (
                  <>
                    <div className="collection-resources-employee-filter-title">
                      Results for Persona: {filteredLinkRecords?.length}
                    </div>
                    <PersonaCard
                      persona={
                        personas.find((p) => p.id === selectedPersonaId)!
                      }
                      onClick={() => {}}
                      showCloseButton={true}
                      onClose={clearPersonaFilter}
                    />
                  </>
                )
              )}
              {selectedEmployeeId && !isLoadingEmployeeLinks ? (
                <>
                  <div className="collection-resources-employee-filter-title">
                    Results for Employee: {filteredLinkRecords?.length}
                  </div>
                  <SelectedEmployeeCard
                    employeeId={selectedEmployeeId}
                    showCloseButton={true}
                    onClose={clearEmployeeFilter}
                    onAskAs={(employeeId) => {
                      if (mergeConnected) {
                        dispatch(setSelectedMenu(MENUS.COLLECTION_ASK));
                        dispatch(setSelectedMenuId(MENUS.COLLECTION_ASK));
                        navigate(
                          `/collection/${currentCollection?.id}/ask?employeeId=${employeeId}`
                        );
                      } else {
                        toast("Merge is not connected", toastFail);
                      }
                    }}
                  />
                </>
              ) : selectedEmployeeId && isLoadingEmployeeLinks ? (
                <div className="loading-spinner">Loading...</div>
              ) : null}
            </div>
          </>
        )}

      {filteredLinkRecords && filteredLinkRecords.length > 0 && (
        <div className="collection-resources-actions">
          <div className="collection-resources-action">
            <button onClick={handleCopyCollectionToClipboard}>
              Copy to Clipboard
            </button>
          </div>
          {config.showLinkRecordLogs && (
            <div className="collection-resources-action">
              <LogsIcon
                onClick={() => {
                  setSelectedLinkRecordId(undefined);
                  dispatch(
                    setUIStates({ ...uiStates, resourceSidebarOpen: true })
                  );
                }}
              />
            </div>
          )}
        </div>
      )}

      {filteredLinkRecords && filteredLinkRecords.length > 0 && (
        <div className="collection-resources-list">
          {filteredLinkRecords.map((linkRecord: LinkRecordFull) => (
            <ResourceCard
              key={linkRecord.id}
              linkRecord={linkRecord}
              onDelete={() => {
                setDeletingLinkRecord(linkRecord);
                setShowDeleteConfirm(true);
              }}
              onOpen={() => handleOpenLink(linkRecord)}
              onFolderDelete={
                linkRecord.folderRootId
                  ? () => deleteFolder(linkRecord)
                  : undefined
              }
              personas={personas}
              personaLinkRecords={personaLinkRecords}
              onAddPersona={(personaId) =>
                handleAddPersona(linkRecord.id, personaId)
              }
              onRemovePersona={(personaId) =>
                handleRemovePersona(linkRecord.id, personaId)
              }
              onTogglePersonaOrientation={(personaId) =>
                handleToggleOrientation(linkRecord.id, personaId)
              }
              showPersonaSelector={showPersonaSelector}
              setShowPersonaSelector={setShowPersonaSelector}
              openResourceActionSidebar={(lrid: string) => {
                setSelectedLinkRecordId(lrid);
                dispatch(
                  setUIStates({
                    ...uiStates,
                    resourceSidebarOpen: true,
                  })
                );
              }}
            />
          ))}
        </div>
      )}

      {/* {filteredLinkRecords &&
        filteredLinkRecords.length < 1 &&
        debouncedSearchText.length === 0 && <CollectionResourcesZero />} */}

      {showEmployeeSearch && (
        <ModalEmployeeResourceSearch
          isOpen={showEmployeeSearch}
          onClose={() => setShowEmployeeSearch(false)}
          onSelect={handleEmployeeSearchSelect}
          employees={employees}
        />
      )}

      {showDeleteConfirm && (
        <ModalDeleteResourceConfirm
          isOpen={showDeleteConfirm}
          onClose={() => {
            setDeletingLinkRecord(null);
            setShowDeleteConfirm(false);
          }}
          onContinue={() => {
            setShowDeleteConfirm(false);
            deleteResource();
          }}
        />
      )}

      {showDeleteAllConfirm && (
        <ModalDeleteAllResourcesConfirm
          isOpen={showDeleteAllConfirm}
          onClose={() => {
            setDeletingLinkRecord(null);
            setShowDeleteAllConfirm(false);
          }}
          onDelete={() => {
            setShowDeleteAllConfirm(false);
            deleteAllResources();
          }}
        />
      )}

      <ModalHRISRequired
        isOpen={showHRISRequired}
        onClose={() => setShowHRISRequired(false)}
        onContinue={() => setShowHRISRequired(false)}
      />

      {currentCollection?.id && (
        <ResourceActionsSidebar
          collectionId={currentCollection.id}
          linkRecordId={selectedLinkRecordId}
        />
      )}
    </div>
  );
};
